import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Component, ErrorInfo, ReactNode } from 'react';
import './index.css';
import App from './App.tsx';
import { HelmetProvider } from 'react-helmet-async';
import { initAnalytics } from './lib/analytics';

// Initialize Analytics
initAnalytics();

// Global Error Boundary to catch and display startup crashes
class ErrorBoundary extends Component<
    { children: ReactNode },
    { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }
> {
    constructor(props: { children: ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('React Error Boundary caught an error:', error, errorInfo);
        this.setState({ errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    minHeight: '100vh',
                    background: '#0f172a',
                    color: '#f1f5f9',
                    padding: '2rem',
                    fontFamily: 'monospace'
                }}>
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <h1 style={{ color: '#ef4444', fontSize: '2rem', marginBottom: '1rem' }}>
                            ⚠️ Application Error
                        </h1>
                        <div style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
                            <h2 style={{ color: '#f59e0b', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                                Error: {this.state.error?.message}
                            </h2>
                            <pre style={{ color: '#94a3b8', fontSize: '0.875rem', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                {this.state.error?.stack}
                            </pre>
                        </div>
                        {this.state.errorInfo && (
                            <div style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px' }}>
                                <h3 style={{ color: '#8b5cf6', marginBottom: '0.5rem' }}>Component Stack:</h3>
                                <pre style={{ color: '#94a3b8', fontSize: '0.875rem', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </div>
                        )}
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                marginTop: '1.5rem',
                                padding: '0.75rem 1.5rem',
                                background: '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            Reload Application
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Log successful startup
console.log('🚀 BayWhiz Frontend Starting...');
console.log('Environment:', import.meta.env.MODE);
console.log('Build Time:', new Date().toISOString());

const rootElement = document.getElementById('root');

if (!rootElement) {
    console.error('❌ FATAL: Root element not found in DOM');
    document.body.innerHTML = `
    <div style="min-height:100vh;background:#0f172a;color:#f1f5f9;padding:2rem;font-family:monospace;display:flex;align-items:center;justify-content:center">
      <div style="max-width:600px;text-align:center">
        <h1 style="color:#ef4444;font-size:2rem;margin-bottom:1rem">⚠️ Fatal Error</h1>
        <p style="color:#94a3b8;font-size:1.2rem">Root element '#root' not found in index.html</p>
        <p style="color:#64748b;margin-top:1rem">Please check the HTML template.</p>
      </div>
    </div>
  `;
} else {
    console.log('✅ Root element found, mounting React app...');

    try {
        createRoot(rootElement).render(
            <StrictMode>
                <ErrorBoundary>
                    <HelmetProvider>
                        <App />
                    </HelmetProvider>
                </ErrorBoundary>
            </StrictMode>
        );
        console.log('✅ React app mounted successfully');
    } catch (error) {
        console.error('❌ Failed to mount React app:', error);
        rootElement.innerHTML = `
      <div style="min-height:100vh;background:#0f172a;color:#f1f5f9;padding:2rem;font-family:monospace;display:flex;align-items:center;justify-content:center">
        <div style="max-width:600px;text-align:center">
          <h1 style="color:#ef4444;font-size:2rem;margin-bottom:1rem">⚠️ Mount Error</h1>
          <p style="color:#94a3b8;font-size:1.2rem">Failed to initialize React application</p>
          <pre style="color:#64748b;margin-top:1rem;text-align:left;overflow:auto">${error}</pre>
        </div>
      </div>
    `;
    }
}
